<template>
    <div class="group-video-call-banner" :class="{ 'expanded': isExpanded }">
        <div class="banner-header">
            <div class="banner-left" @click.stop="switchExpand">
                <div class="call-icon">
                    <i class="el-icon-video-camera"></i>
                </div>
                <div class="header-avatars">
                    <head-image v-for="(user, index) in headerUsers" :key="user.id" :id="user.id" :url="user.headImage"
                        :name="resolveUserName(user)" :size="28" @click.native.stop="showUserInfo($event, user.id)"
                        :class="['header-avatar-item', { 'avatar-overlap': index > 0, 'is-disabled': !canViewMemberProfile || pendingUserId === user.id }]"></head-image>
                    <div v-if="hasMoreUsers" class="avatar-more">
                        <span>+{{ moreCount }}</span>
                    </div>
                </div>
                <div class="call-info">
                    <div class="call-text">{{ callText }}</div>
                    <div class="call-summary">{{ summaryText }}</div>
                </div>
            </div>
            <div class="banner-actions">
                <el-button size="mini" type="primary" round @click.stop="onJoin">加入通话</el-button>
                <button type="button" class="toggle-btn" :class="{ 'expanded': isExpanded }" @click.stop="switchExpand">
                    <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </button>
            </div>
        </div>

        <div v-if="isExpanded" class="banner-content">
            <div class="member-title">当前通话成员</div>
            <div class="user-avatars">
                <head-image v-for="user in displayUsers" :key="user.id" :id="user.id" :url="user.headImage"
                    :name="resolveUserName(user)" :size="48" @click.native.stop="showUserInfo($event, user.id)"
                    :class="['avatar-item', { 'is-disabled': !canViewMemberProfile || pendingUserId === user.id }]"></head-image>
            </div>
        </div>
    </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';

export default {
    name: "ChatGroupRtcBanner",
    components: {
        HeadImage
    },
    props: {
        rtcInfo: {
            type: Object,
            required: true
        },
        groupId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            isExpanded: false,
            pendingUserId: null
        }
    },
    computed: {
        canViewMemberProfile() {
            if (!this.groupId) {
                return true;
            }
            return this.$permission.hasGroupPermission(this.groupId, 'isAllowViewMemberProfile');
        },
        displayUsers() {
            return this.normalizedUsers;
        },
        headerUsers() {
            return this.normalizedUsers.slice(0, 3);
        },
        hasMoreUsers() {
            return this.moreCount > 0;
        },
        moreCount() {
            return Math.max(this.normalizedUsers.length - 3, 0);
        },
        callText() {
            const count = this.normalizedUsers.length;
            return count > 0 ? `${count} 人正在群通话` : '群通话进行中';
        },
        summaryText() {
            if (this.displayUsers.length > 0) {
                return '点击展开查看成员，加入后将直接进入当前群通话。';
            }
            return '当前群聊存在进行中的通话，点击加入即可接入。';
        },
        normalizedUsers() {
            const users = Array.isArray((this.rtcInfo || {}).userInfos) ? this.rtcInfo.userInfos : [];
            return users
                .filter(user => user && user.id && (user.mediaReady || user.mediaUserId))
                .map(user => ({
                    ...user,
                    nickName: this.resolveUserName(user),
                    headImage: user.headImage || ''
                }));
        }
    },
    methods: {
        resolveUserName(user) {
            if (!user) {
                return '当前成员';
            }
            return user.showNickName || user.nickName || user.userName || '当前成员';
        },
        switchExpand() {
            this.isExpanded = !this.isExpanded;
        },
        onJoin() {
            this.$emit('join');
        },
        showUserInfo(e, userId) {
            if (!this.canViewMemberProfile) {
                this.$message.warning('群资料已设置为不可查看成员资料');
                return;
            }
            if (!userId || this.pendingUserId === userId) {
                return;
            }
            this.pendingUserId = userId;
            this.$http({
                url: `/user/find/${userId}`,
                method: 'get'
            }).then((user) => {
                let pos = {
                    x: e.x + 30,
                    y: e.y
                }
                this.$eventBus.$emit("openUserInfo", user, pos);
            }).catch((error) => {
                this.$message.error(error && error.message ? error.message : '成员资料加载失败');
            }).finally(() => {
                if (this.pendingUserId === userId) {
                    this.pendingUserId = null;
                }
            })
        }
    }
}
</script>

<style scoped lang="scss">
.group-video-call-banner {
    border-radius: 18px;
    border: 1px solid rgba(var(--im-primary-rgb), 0.12);
    background: linear-gradient(135deg, rgba(var(--im-primary-rgb), 0.08) 0%, var(--im-surface-panel-strong) 32%, var(--im-surface-panel) 100%);
    box-shadow: 0 16px 28px -28px rgba(var(--im-primary-rgb), 0.7);
    backdrop-filter: blur(18px);
    transition: all 0.3s ease;

    .banner-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 14px;
        transition: background 0.2s ease;

        .banner-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            min-width: 0;

            .call-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 42px;
                height: 42px;
                flex-shrink: 0;
                position: relative;
                border-radius: 14px;
                background: rgba(var(--im-primary-rgb), 0.08);

                i {
                    font-size: 20px;
                    color: var(--im-color-primary);
                    animation: pulse 3s ease-in-out infinite;
                    position: relative;
                    z-index: 1;
                }

                // 脉冲波纹效果
                &::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: var(--im-color-primary);
                    opacity: 0.15;
                    animation: ripple 3s ease-out infinite;
                }

                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: var(--im-color-primary);
                    opacity: 0.1;
                    animation: ripple 3s ease-out 1.5s infinite;
                }
            }

            @keyframes pulse {

                0%,
                100% {
                    transform: scale(1);
                }

                50% {
                    transform: scale(1.03);
                }
            }

            @keyframes ripple {
                0% {
                    transform: translate(-50%, -50%) scale(1);
                    opacity: 0.3;
                }

                100% {
                    transform: translate(-50%, -50%) scale(1.5);
                    opacity: 0;
                }
            }

            .header-avatars {
                display: flex;
                align-items: center;
                flex-shrink: 0;
                position: relative;

                .header-avatar-item {
                    border: 2px solid var(--im-surface-panel-strong);
                    border-radius: 50%;
                    transition: transform 0.2s ease;
                    position: relative;
                    z-index: 1;
                    cursor: pointer;

                    &.avatar-overlap {
                        margin-left: -8px;
                    }

                    &:hover {
                        transform: scale(1.1);
                        z-index: 10;
                    }

                    &.is-disabled {
                        cursor: wait;
                        opacity: 0.7;
                    }

                    &:first-child {
                        z-index: 1;
                    }

                    &:nth-child(2) {
                        z-index: 2;
                    }

                    &:nth-child(3) {
                        z-index: 3;
                    }
                }

                .avatar-more {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: var(--im-surface-panel-soft);
                    border: 2px solid var(--im-surface-panel-strong);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: -8px;
                    font-size: 12px;
                    color: var(--im-text-color-light);
                    font-weight: 500;
                    position: relative;
                    z-index: 4;
                }
            }

            .call-info {
                flex: 1;
                min-width: 0;

                .call-text {
                    font-size: var(--im-font-size-small);
                    color: var(--im-text-color);
                    font-weight: 600;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .call-summary {
                    margin-top: 2px;
                    font-size: var(--im-font-size-smaller);
                    line-height: 1.4;
                    color: var(--im-text-color-light);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        }

        .banner-actions {
            display: flex;
            align-items: center;
            gap: 8px;

            .toggle-btn {
                width: 30px;
                height: 30px;
                border: 0;
                border-radius: 50%;
                background: rgba(var(--im-primary-rgb), 0.08);
                color: var(--im-text-color-light);
                cursor: pointer;
                transition: all 0.2s ease;

                &.expanded {
                    transform: rotate(180deg);
                }

                &:hover {
                    background: rgba(var(--im-primary-rgb), 0.14);
                }
            }
        }
    }

    .banner-content {
        padding: 12px 14px 14px;
        background: linear-gradient(180deg, rgba(var(--im-primary-rgb), 0.04) 0%, var(--im-surface-panel-soft) 100%);
        border-top: 1px solid var(--im-surface-panel-border);
        display: flex;
        flex-direction: column;
        gap: 12px;
        animation: slideDown 0.3s ease;

        .member-title {
            font-size: var(--im-font-size-small);
            color: var(--im-text-color-light);
            text-align: left;
        }

        @keyframes slideDown {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }

            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .user-avatars {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 12px;

            .avatar-item {
                transition: transform 0.2s ease;

                &:hover {
                    transform: scale(1.05);
                }

                &.is-disabled {
                    cursor: wait;
                    opacity: 0.7;
                }
            }
        }
    }
}
</style>
